/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { MoreHorizontal, Download, Eye, Printer, Search, Filter, FileText, X, ChevronLeft, ChevronRight } from "lucide-react";
import BillingHistoryCards from "./_bilinghistorycards";
import { useGetUtilitiesSummaryDataQuery } from "../../lib/apiSlice/property/propertySlice.tsx";
import UtilitiesDashboardStats from "./_utilitiesdashboardstats.jsx";

export function BillingHistory() {
  const { data, isLoading, error } = useGetUtilitiesSummaryDataQuery();
  const [bills, setBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProperty, setFilterProperty] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (data?.expense_bill_data) {
      setBills(data.expense_bill_data);
    }
  }, [data]);

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case "paid":
        return (
          <span className="inline-flex items-center bg-green-100 px-2.5 py-0.5 rounded-full font-medium text-green-800 text-xs">
            Paid
          </span>
        );
      case "partial":
        return (
          <span className="inline-flex items-center bg-yellow-100 px-2.5 py-0.5 rounded-full font-medium text-yellow-800 text-xs">
            Partial
          </span>
        );
      case "unpaid":
        return (
          <span className="inline-flex items-center bg-red-100 px-2.5 py-0.5 rounded-full font-medium text-red-800 text-xs">
            Unpaid
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center bg-gray-100 px-2.5 py-0.5 rounded-full font-medium text-gray-800 text-xs">
            {status}
          </span>
        );
    }
  };

  const handleViewBill = (bill) => {
    setSelectedBill(bill);
    setIsViewDialogOpen(true);
  };

  // CSV Export Function
  const exportToCSV = () => {
    if (!filteredBills || filteredBills.length === 0) {
      alert("No data to export");
      return;
    }

    // CSV Headers
    const headers = [
      'Invoice ID',
      'Property',
      'Unit',
      'Tenant Name',
      'Tenant Phone',
      'Amount (KES)',
      'Invoice Date',
      'Status'
    ];

    // Convert bills data to CSV format
    const csvData = filteredBills.map(bill => [
      `INV ${bill.invoice_id}`,
      bill.property || '',
      bill.house || '',
      bill.tenant_name || '',
      bill.tenant_phone || '',
      parseInt(bill.invoice_amount)?.toLocaleString('en-US') || '0',
      `${bill.invoice_month?.toUpperCase() || ''}, ${bill.invoice_year || ''}`,
      bill.invoice_status || ''
    ]);

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `billing_history_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const filteredBills = bills?.filter((bill) => {
    const matchesSearch =
      bill.invoice_id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.property?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.house?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === "all" || bill.invoice_status?.toLowerCase() === filterStatus.toLowerCase();
    const matchesMonth = filterMonth === "all" || bill.invoice_month?.toLowerCase() === filterMonth.toLowerCase();
    const matchesProperty = filterProperty === "all" ||
      (data?.landlord_expenses?.find(apt => apt.property_id === filterProperty)?.property_id === bill.property_id);


    const matchesTab =
      activeTab === "all" ||
      (activeTab === "pending" && bill.invoice_status === "Pending") ||
      (activeTab === "overdue" && bill.invoice_status === "Overdue") ||
      (activeTab === "paid" && bill.invoice_status === "Paid");

    return matchesSearch && matchesStatus && matchesProperty && matchesTab && matchesMonth;
  }) || [];

  // Pagination logic
  const totalPages = Math.ceil(filteredBills.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBills.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Dynamic invoice calculation function
  const calculateInvoiceDetails = (bill) => {
    const baseAmount = parseInt(bill.invoice_amount) || 0;

    // Calculate breakdown (you can adjust these percentages/values based on your business logic)
    const waterConsumption = Math.round(baseAmount * 0.4); // 40% for water
    const electricityConsumption = Math.round(baseAmount * 0.35); // 35% for electricity
    const garbageCollection = 500; // Fixed amount
    const securityFee = 1000; // Fixed amount

    const subtotal = waterConsumption + electricityConsumption + garbageCollection + securityFee;
    const vat = Math.round(subtotal * 0.16); // 16% VAT
    const total = subtotal + vat;

    return {
      waterConsumption,
      electricityConsumption,
      garbageCollection,
      securityFee,
      subtotal,
      vat,
      total: baseAmount // Use the actual bill amount as total
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex md:flex-row flex-col justify-between md:items-center gap-4">
        <div>
          <h2 className="font-bold text-blue-700 text-xl">Billing History</h2>
          <p className="text-gray-500">View and manage all utility bills and invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportToCSV}
            className="inline-flex items-center bg-white hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-700 text-sm"
          >
            <Download className="mr-2 w-4 h-4 text-blue-600" />
            Export
          </button>
        </div>
      </div>
      <UtilitiesDashboardStats stats={data?.landlord_total_metrics} loading={isLoading} />

      {/* <BillingHistoryCards /> */}

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <div className="p-4 border-gray-200 border-b">
          <div className="flex md:flex-row flex-col justify-between md:items-center gap-4">
            <div>
              <h3 className="font-medium text-gray-900 text-lg">All Bills</h3>
              <p className="text-gray-500 text-sm">View and manage all utility bills across properties</p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex md:flex-row flex-col justify-between md:items-center gap-4 mb-4">
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="relative w-full md:w-[300px]">
                <div className="left-0 absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                  <Search className="w-4 h-4 text-blue-600" />
                </div>
                <input
                  type="search"
                  placeholder="Search bills..."
                  className="py-2 pr-3 pl-10 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-sm"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                  }}
                />
              </div>
            </div>
            <div className="flex md:flex-row flex-col items-center gap-2">
              <div className="relative w-full md:w-[180px]">
                <select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1); // Reset to first page when filtering
                  }}
                  className="bg-white py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full h-10 text-sm appearance-none"
                >
                  <option value="all">All Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>

              <div className="flex md:flex-row flex-col gap-4 w-full">
                {/* Property Filter */}
                <div className="relative w-full md:w-[180px]">
                  <select
                    value={filterProperty}
                    onChange={(e) => {
                      setFilterProperty(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="bg-white py-2 pr-10 pl-3 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full h-10 text-sm appearance-none"
                  >
                    <option value="all">All Properties</option>
                    {data?.landlord_expenses?.map((apartment, index) => (
                      <option key={index} value={apartment.apartment_name}>
                        {apartment.apartment_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month-Year Filter */}
                {/* <div className="relative w-full md:w-[180px]">
                  <input
                    type="month"
                    value={filterMonth}
                    onChange={(e) => {
                      setFilterMonth(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="bg-white px-3 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full h-10 text-sm"
                  />
                </div> */}
              </div>


            </div>
          </div>
          <div className="border border-gray-200 rounded-md overflow-auto">
            <table className="divide-y divide-gray-200 min-w-full">
              <thead className="bg-blue-50">
                <tr>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Invoice #</th>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Property</th>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Unit</th>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Tenant Name</th>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Tenant Phone</th>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Amount</th>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Invoice Date</th>
                  <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Status</th>
                  {/* <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Actions</th> */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <>
                    {[...Array(4)].map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        {Array(9).fill(null).map((_, idx) => (
                          <td key={idx} className="px-6 py-4">
                            <div className="bg-gray-200 rounded w-3/4 h-4"></div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-8 text-gray-500 text-sm text-center">
                      No bills found matching your criteria
                    </td>
                  </tr>
                ) : (
                  currentItems.map((bill) => (
                    <tr key={bill.invoice_id} >
                      <td className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">INV {bill.invoice_id}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{bill.property}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{bill.house}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{bill.tenant_name}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{bill.tenant_phone}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                        KES {parseInt(bill.invoice_amount)?.toLocaleString('en-US')}
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">
                        {bill.invoice_month?.toUpperCase()}, {bill.invoice_year}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(bill.invoice_status)}</td>
                      {/* <td className="px-6 py-4 font-medium text-sm text-right whitespace-nowrap">
                        <button
                          onClick={() => handleViewBill(bill)}
                          className="flex items-center hover:bg-blue-50 px-4 py-2 w-full text-blue-600 text-sm"
                        >
                          <Eye className="mr-2 w-4 h-4" />
                          View
                        </button>
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {filteredBills.length > itemsPerPage && (
            <div className="flex justify-between items-center mt-4">
              <div className="text-gray-500 text-sm">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastItem, filteredBills.length)}
                </span>{" "}
                of <span className="font-medium">{filteredBills.length}</span> results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`p-2 border rounded-md ${currentPage === 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50"}`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`px-3 py-1 border rounded-md ${currentPage === number ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    {number}
                  </button>
                ))}
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`p-2 border rounded-md ${currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50"}`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* View Bill Dialog with Dynamic Data */}
      {isViewDialogOpen && selectedBill && (
        <div className="z-50 fixed inset-0 bg-black/50 overflow-y-auto">
          <div className="sm:block flex justify-center items-center sm:p-0 px-4 pt-4 pb-20 min-h-screen text-center">
            {/* Overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div
                className="absolute inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity duration-300"
                onClick={() => setIsViewDialogOpen(false)}
              ></div>
            </div>

            {/* Modal Container */}
            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block bg-white shadow-xl sm:my-8 rounded-lg w-full sm:w-full max-w-lg sm:max-w-4xl overflow-hidden sm:align-middle transition-all transform">
              {/* Header */}
              <div className="bg-blue-600 px-6 py-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-white text-lg">
                    Invoice #{selectedBill.invoice_id}
                  </h3>
                  <button
                    onClick={() => setIsViewDialogOpen(false)}
                    className="text-white hover:text-blue-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="mt-1 text-blue-100 text-sm">
                  Utility bill for {selectedBill.invoice_month?.toUpperCase()}, {selectedBill.invoice_year}
                </p>
              </div>

              {/* Content */}
              <div className="bg-white px-6 py-5">
                <div className="space-y-6">
                  {/* Company and Invoice Info */}
                  <div className="flex sm:flex-row flex-col justify-between gap-6">
                    <div>
                      <h3 className="font-fonarto font-bold text-blue-600 text-3xl" style={{ fontFamily: "'Fonarto', sans-serif" }}
                      >Kodi</h3>
                      <div className="space-y-1 mt-2 text-gray-600 text-sm">

                      </div>
                    </div>
                    <div className="text-right">
                      <h3 className="font-bold text-blue-700 text-lg">INVOICE</h3>
                      <div className="space-y-1 mt-2 text-gray-600 text-sm">
                        <p>Invoice #:  {selectedBill.invoice_id}</p>
                        <p>Date: {selectedBill.invoice_month?.toUpperCase()}, {selectedBill.invoice_year}</p>
                      </div>
                      <div className="inline-block mt-3">
                        {getStatusBadge(selectedBill.invoice_status)}
                      </div>
                    </div>
                  </div>

                  {/* Bill To */}
                  <div className="pt-6 border-gray-200 border-t">
                    <h4 className="mb-3 font-semibold text-gray-700 text-sm uppercase tracking-wider">Bill To:</h4>
                    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                      <div>
                        <p className="font-medium text-gray-900">{selectedBill.tenant_name}</p>
                        <p className="text-gray-600 text-sm">
                          {selectedBill.house}, {selectedBill.property}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm">Phone: {selectedBill.tenant_phone}</p>
                        <p className="text-gray-600 text-sm">Nairobi, Kenya</p>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Items */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="divide-y divide-gray-200 min-w-full">
                      <thead className="bg-blue-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                            Description
                          </th>
                          <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                            Consumption
                          </th>
                          <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">
                            Rate
                          </th>
                          <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-right uppercase tracking-wider">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(() => {
                          const invoiceDetails = calculateInvoiceDetails(selectedBill);
                          return (
                            <>
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">Water Consumption</td>
                                <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">{Math.round(invoiceDetails.waterConsumption / 120)} m³</td>
                                <td className="px-6 py-4 text-gray-500 text-sm whitespace-nowrap">KSh 120/m³</td>
                                <td className="px-6 py-4 text-gray-500 text-sm text-right whitespace-nowrap">
                                  KSh {invoiceDetails.waterConsumption.toLocaleString('en-US')}
                                </td>
                              </tr>
                            
                              <tr className="bg-gray-50">
                                <td colSpan={3} className="px-6 py-4 font-semibold text-gray-700 text-sm text-right">
                                  Subtotal
                                </td>
                                <td className="px-6 py-4 text-gray-500 text-sm text-right whitespace-nowrap">
                                  KSh {invoiceDetails.subtotal.toLocaleString('en-US')}
                                </td>
                              </tr>
                              <tr className="bg-gray-50">
                                <td colSpan={3} className="px-6 py-4 font-semibold text-gray-700 text-sm text-right">
                                  VAT (16%)
                                </td>
                                <td className="px-6 py-4 text-gray-500 text-sm text-right whitespace-nowrap">
                                  KSh {invoiceDetails.vat.toLocaleString('en-US')}
                                </td>
                              </tr>
                              <tr className="bg-blue-50 border-gray-200 border-t">
                                <td colSpan={3} className="px-6 py-4 font-bold text-gray-900 text-sm text-right">
                                  Total Amount Due
                                </td>
                                <td className="px-6 py-4 font-bold text-blue-700 text-sm text-right whitespace-nowrap">
                                  KSh {parseInt(selectedBill.invoice_amount)?.toLocaleString('en-US')}
                                </td>
                              </tr>
                            </>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {/* Payment Methods */}
                  {/* <div className="pt-6 border-gray-200 border-t">
                    <h4 className="mb-3 font-semibold text-gray-700 text-sm uppercase tracking-wider">Payment Methods:</h4>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-start">
                          <span className="inline-block bg-blue-100 mr-3 p-1 rounded-full text-blue-800">
                            <span className="block w-4 h-4">1</span>
                          </span>
                          <span>M-Pesa Paybill: 123456, Account: {selectedBill.house}</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block bg-blue-100 mr-3 p-1 rounded-full text-blue-800">
                            <span className="block w-4 h-4">2</span>
                          </span>
                          <span>Bank Transfer: Nyumba Properties, Acc #: 1234567890, KCB Bank</span>
                        </li>
                        <li className="flex items-start">
                          <span className="inline-block bg-blue-100 mr-3 p-1 rounded-full text-blue-800">
                            <span className="block w-4 h-4">3</span>
                          </span>
                          <span>Cash/Cheque: At the property management office</span>
                        </li>
                      </ul>
                    </div>
                  </div> */}

                  {/* Footer Note */}
                  <div className="pt-6 border-gray-200 border-t text-gray-600 text-sm">
                    <p className="mb-2">Thank you for your business. Please make payment by the due date.</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex sm:flex-row-reverse flex-col sm:justify-start gap-3 bg-gray-50 px-6 py-4">

                <button
                  type="button"
                  className="inline-flex justify-center items-center shadow-sm sm:mr-auto px-4 py-2 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 w-full sm:w-auto font-medium text-gray-700 hover:text-gray-900 text-sm transition-colors"
                  onClick={() => setIsViewDialogOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}